import axios from 'axios';
import qs from 'qs';

function createApi() {
  const apiClient = axios.create({
    baseURL: process.env.BACKEND_URL,
    responseType: 'json'
  });

  const setAuthHeader = authToken => {
    if (authToken) {
      apiClient.defaults.headers.common[
        'Authorization'
      ] = `bearer ${authToken}`;
    } else {
      delete apiClient.defaults.headers.common['Authorization'];
    }
  };
  const loginWithEmail = async ({ email, password }) => {
    const { data } = await apiClient.post('/auth/login', {
      email,
      password
    });
    return data;
  };

  const loginWithSocial = async ({ type, token }) => {
    const { data } = await apiClient.post('/auth/sso', {
      type,
      token
    });
    return data;
  };

  const checkEmailAvailability = async ({ email }) => {
    const { data } = await apiClient.post('/auth/check', { email });

    const {
      results: { isValid }
    } = data;

    if (!isValid) {
      throw Error('Email is already used');
    }

    return data;
  };

  const reigsterWithEmail = async ({
    firstName,
    lastName,
    phoneNumber,
    email,
    password,
    ...rest
  }) => {
    const { data } = await apiClient.post('/auth/signup', {
      firstName,
      lastName,
      phone: phoneNumber,
      email,
      password,
      ...rest
    });
    return data;
  };

  const forgotPassword = async ({ email }) => {
    const { data } = await apiClient.post('/auth/password/forgot', {
      email
    });
    return data;
  };

  const resetPassword = async values => {
    const { data } = await apiClient.post('/auth/password/reset', values);
    return data;
  };

  const updateCurrentPassword = async ({
    currentPassword,
    password,
    passwordConfirmation
  }) => {
    const { data } = await apiClient.patch('/me/password', {
      currentPassword,
      password,
      passwordConfirmation
    });
    return data;
  };

  const getAuthToken = async ({ refreshToken, authToken }) => {
    setAuthHeader(authToken);
    const { data } = await apiClient.post('/auth/token', {
      refreshToken
    });
    return data;
  };
  const getUserProfile = async () => {
    const { data } = await apiClient.get('/me');
    return data;
  };

  const updateBasicProfile = async (values, authToken) => {
    if (authToken) {
      setAuthHeader(authToken);
    }

    const { data } = await apiClient.put('/me/basic', values);
    return data;
  };

  const getProperties = async name => {
    const { data } = await apiClient.get(
      `/properties?${qs.stringify({ name })}`
    );
    return data;
  };

  const getPropertyById = async propertyId => {
    const { data } = await apiClient.get(`/properties/${propertyId}`);

    return data;
  };

  const getPropertyFromGoogleId = async googlePlaceId => {
    const { data } = await apiClient.get(`/properties/place/${googlePlaceId}`);
    return data;
  };

  const assignProperty = async values => {
    const { data } = await apiClient.patch('/me/property/assign', values);
    return data;
  };
  const getAppData = async () => {
    const { data } = await apiClient.get('/app/data');
    return data;
  };
  const getSubscriptions = async () => {
    const { data } = await apiClient.get('/recurring-bookings');
    return data;
  };
  const getMyCleans = async (month = 9, year = 2019) => {
    const { data } = await apiClient.get(
      `/bookings?${qs.stringify({ month, year })}`
    );
    return data;
  };

  const getMyLatestCleans = async () => {
    const { data } = await apiClient.get(
      `/bookings/list?${qs.stringify({ limit: 10, offset: 0 })}`
    );
    return data;
  };

  const setCleanRate = async (bookingId, rateData) => {
    const { data } = await apiClient.patch(
      `/bookings/${bookingId}/rate`,
      rateData
    );
    return data;
  };

  const getCleanDetail = async bookingId => {
    const { data } = await apiClient.get(`/bookings/${bookingId}`);
    return data;
  };

  const cancelClean = async bookingId => {
    const { data } = await apiClient.post(`/bookings/cancel/${bookingId}`);
    return data;
  };

  const upgradeClean = async bookingId => {
    const { data } = await apiClient.post(`/bookings/${bookingId}/upgrade`);
    return data;
  };

  const getPropertySettingStatus = async () => {
    const { data } = await apiClient.get('/properties/settings/status');
    return data;
  };

  const getKeyReleaseInfo = async propertyId => {
    const { data } = await apiClient.get(
      `/me/key-release?propertyID=${propertyId}`
    );
    return data;
  };

  const setKeyReleaseInfo = async ({ propertyID, firstName, lastName }) => {
    const { data } = await apiClient.post('/me/key-release', {
      propertyID,
      signature: `${firstName} ${lastName}`
    });
    return data;
  };

  const getAvailableDates = async (values, isPM, type = 'single') => {
    let queryParams = { type, ...values };
    const { data } = await apiClient.get(
      `/bookings/available-dates?${qs.stringify(queryParams)}`
    );
    return data;
  };

  const addPayment = async body => {
    const { data } = await apiClient.patch('/me/payment', { ...body });
    return data;
  };

  const getPMPricings = async ({ propertyID, bedroomsCount }) => {
    const { data } = await apiClient.get(
      `/bookings/cost?propertyID=${propertyID}&bedroomsCount=${bedroomsCount}`
    );
    return data;
  };

  const getPMCostCalculations = async (values, recipient) => {
    const { propertyID, bedroomsCount } = recipient;

    const { data } = await apiClient.post('/bookings/cost-calculations', {
      ...values,
      propertyID,
      bedroomsCount
    });
    return data;
  };

  const getCostCalculations = async valuesData => {
    const { isPMUser, recipient, ...values } = valuesData;
    if (isPMUser) {
      return getPMCostCalculations(values, recipient);
    }
    const { data } = await apiClient.post(
      '/bookings/cost-calculations',
      values
    );
    return data;
  };

  const getPMRecurringCostCalculations = async (values, recipient) => {
    const { propertyID, bedroomsCount } = recipient;

    const { data } = await apiClient.post(
      '/recurring-bookings/cost-calculations',
      {
        ...values,
        propertyID,
        bedroomsCount
      }
    );
    return data;
  };

  const getRecurringCostCalculations = async valuesData => {
    const { isPMUser, recipient, ...values } = valuesData;

    if (isPMUser) {
      return getPMRecurringCostCalculations(values, recipient);
    }
    const { data } = await apiClient.post(
      '/recurring-bookings/cost-calculations',
      values
    );
    return data;
  };

  const getModifyingCleanCostCalculations = async (bookingId, valuesData) => {
    const { isPMUser, recipient, ...values } = valuesData;
    let extraData = {};
    if (isPMUser) {
      const { propertyID, bedroomsCount } = recipient;
      extraData = { ...extraData, propertyID, bedroomsCount };
    }
    const { data } = await apiClient.post(
      `/bookings/cost-calculations/${bookingId}`,
      {
        ...extraData,
        ...values
      }
    );
    return data;
  };

  const getBillingHistory = async () => {
    const { data } = await apiClient.get(
      `/bookings/list?${qs.stringify({ limit: 10, offset: 0 })}`
    );
    return data;
  };

  const updateBooking = async (bookingId, valuesData) => {
    // eslint-disable-next-line no-unused-vars
    const { isPMUser, recipient, paymentMethod, ...values } = valuesData;
    if (isPMUser) {
      throw Error('PM Update is not implemented yet');
    }
    const { data } = await apiClient.put(`/bookings/${bookingId}`, values);
    return data;
  };

  const createPMBooking = async (values, recipient, paymentMethod) => {
    // eslint-disable-next-line no-unused-vars
    const { propertyName, ...recipientNoPropName } = recipient;
    const { data } = await apiClient.post('/bookings', {
      ...values,
      ...recipientNoPropName,
      paymentMethod
    });
    return data;
  };

  const createBooking = async valuesData => {
    const { isPMUser, recipient, paymentMethod, ...values } = valuesData;
    if (isPMUser) {
      return createPMBooking(values, recipient, paymentMethod);
    }
    const { data } = await apiClient.post('/bookings', values);
    return data;
  };

  const createRecurringPMBooking = async (values, recipient, paymentMethod) => {
    // eslint-disable-next-line no-unused-vars
    const { propertyName, ...recipientNoPropName } = recipient;
    const { data } = await apiClient.post('/recurring-bookings', {
      ...values,
      ...recipientNoPropName,
      paymentMethod
    });
    return data;
  };

  const createRecurringBooking = async valuesData => {
    const { isPMUser, recipient, paymentMethod, ...values } = valuesData;
    if (isPMUser) {
      return createRecurringPMBooking(values, recipient, paymentMethod);
    }
    const { data } = await apiClient.post('/recurring-bookings', values);
    return data;
  };

  const updateRecurringBooking = async (recurringBookingID, valuesData) => {
    const { data } = await apiClient.patch(
      `/recurring-bookings/${recurringBookingID}`,
      valuesData
    );
    return data;
  };

  const updateBookingNote = async (bookingId, notes) => {
    const { data } = await apiClient.patch(`/bookings/${bookingId}/notes`, {
      notes
    });
    return data;
  };

  const pauseService = async (values, recurringBookingId) => {
    const { data } = await apiClient.post(
      `recurring-bookings/pause/${recurringBookingId}`,
      values
    );
    return data;
  };

  const discontinueService = async values => {
    const { data } = await apiClient.post(
      '/recurring-bookings/discontinue',
      values
    );
    return data;
  };

  const validateCoupon = async (couponAlias, params) => {
    const queryParams = qs.stringify(params);
    const URL = `coupons/validate/${couponAlias}${
      queryParams ? `?${queryParams}` : ''
    }`;
    const { data } = await apiClient.get(URL);

    return data;
  };

  const requestProperty = async values => {
    const { data } = await apiClient.post('/building-request', values);

    return data;
  };

  const moveoutProperty = async values => {
    const { data } = await apiClient.post('/me/property/move-out', values);

    return data;
  };

  return {
    setAuthHeader,
    loginWithEmail,
    loginWithSocial,
    forgotPassword,
    resetPassword,
    checkEmailAvailability,
    updateCurrentPassword,
    reigsterWithEmail,
    getAuthToken,
    getUserProfile,
    updateBasicProfile,
    getAppData,
    getMyCleans,
    getMyLatestCleans,
    getCleanDetail,
    setCleanRate,
    getKeyReleaseInfo,
    setKeyReleaseInfo,
    getAvailableDates,
    addPayment,
    getCostCalculations,
    getRecurringCostCalculations,
    getModifyingCleanCostCalculations,
    createBooking,
    updateBooking,
    createRecurringBooking,
    updateRecurringBooking,
    getSubscriptions,
    pauseService,
    discontinueService,
    getBillingHistory,
    cancelClean,
    upgradeClean,
    getPMPricings,
    updateBookingNote,
    validateCoupon,
    getProperties,
    assignProperty,
    requestProperty,
    getPropertyById,
    getPropertyFromGoogleId,
    getPropertySettingStatus,
    moveoutProperty
  };
}

const apiClient = createApi();

export default apiClient;
